import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiEye, 
  FiUsers, 
  FiMousePointer, 
  FiSettings, 
  FiX, 
  FiBarChart2, 
  FiTrendingUp, 
  FiCheckCircle, 
  FiTrash2 
} = FiIcons;

const AdminPanel = ({ onSave, initialSettings }) => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [settings, setSettings] = useState(initialSettings);
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    buttonClicks: 0,
    conversionRate: 0,
    hourlyViews: Array(24).fill(0),
    deviceBreakdown: {
      desktop: 0,
      mobile: 0,
      tablet: 0,
    },
  });
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedAnalytics = localStorage.getItem('landing_analytics');
    if (savedAnalytics) {
      setAnalytics(JSON.parse(savedAnalytics));
    }
    const interval = setInterval(() => {
      const currentAnalytics = localStorage.getItem('landing_analytics');
      if (currentAnalytics) {
        setAnalytics(JSON.parse(currentAnalytics));
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      localStorage.setItem('landing_settings', JSON.stringify(settings));
      if (onSave) {
        onSave(settings);
      }
      setShowSaveConfirmation(true);
      setTimeout(() => setShowSaveConfirmation(false), 2500);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`Failed to save settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearStats = () => {
    if (window.confirm('Are you sure you want to clear all analytics data? This action cannot be undone.')) {
      const initialAnalytics = {
        totalViews: 0,
        uniqueVisitors: 0,
        buttonClicks: 0,
        conversionRate: 0,
        hourlyViews: Array(24).fill(0),
        deviceBreakdown: {
          desktop: 0,
          mobile: 0,
          tablet: 0,
        },
      };
      localStorage.setItem('landing_analytics', JSON.stringify(initialAnalytics));
      setAnalytics(initialAnalytics);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex flex-1 overflow-hidden">
          <div className="w-56 bg-gray-50 border-r border-gray-200">
            <div className="p-4">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-6 text-gray-700">
                <SafeIcon icon={FiBarChart2} />
                Admin
              </h2>
              <nav className="space-y-2">
                <button 
                  onClick={() => setActiveTab('analytics')} 
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                    activeTab === 'analytics' 
                      ? 'bg-fuchsia-500 text-white' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`} 
                >
                  <SafeIcon icon={FiBarChart2} />
                  Analytics
                </button>
                <button 
                  onClick={() => setActiveTab('settings')} 
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                    activeTab === 'settings' 
                      ? 'bg-fuchsia-500 text-white' 
                      : 'hover:bg-gray-200 text-gray-700'
                  }`} 
                >
                  <SafeIcon icon={FiSettings} />
                  Settings
                </button>
              </nav>
            </div>
          </div>
          <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
            {activeTab === 'analytics' && (
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-800">Analytics Overview</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white border border-gray-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">Total Views</p>
                        <p className="text-3xl font-bold text-blue-800">{analytics.totalViews}</p>
                      </div>
                      <SafeIcon icon={FiEye} className="text-blue-400 text-3xl" />
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">Unique Visitors</p>
                        <p className="text-3xl font-bold text-green-800">{analytics.uniqueVisitors}</p>
                      </div>
                      <SafeIcon icon={FiUsers} className="text-green-400 text-3xl" />
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">Button Clicks</p>
                        <p className="text-3xl font-bold text-purple-800">{analytics.buttonClicks}</p>
                      </div>
                      <SafeIcon icon={FiMousePointer} className="text-purple-400 text-3xl" />
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-600 text-sm font-medium">Conversion Rate</p>
                        <p className="text-3xl font-bold text-orange-800">{analytics.conversionRate}%</p>
                      </div>
                      <SafeIcon icon={FiTrendingUp} className="text-orange-400 text-3xl" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-4 text-gray-700">Hourly Views</h4>
                    <div className="flex items-end justify-between h-48">
                      {analytics.hourlyViews.map((views, hour) => {
                        const maxViews = Math.max(...analytics.hourlyViews, 1);
                        const percentage = (views / maxViews) * 100;
                        const barHeight = views > 0 ? Math.max(percentage, 2) : 0;
                        return (
                          <div 
                            key={hour} 
                            className="flex-1 mx-0.5 text-center flex flex-col justify-end"
                          >
                            <div 
                              className="bg-fuchsia-500 rounded-t transition-all duration-300" 
                              style={{ height: `${barHeight}%` }} 
                              title={`Hour ${hour}: ${views} views`}
                            />
                            <p className="text-xs text-gray-500 mt-1">{hour}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-4 text-gray-700">Device Breakdown</h4>
                    <div className="space-y-4 pt-2">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Desktop</span>
                          <span>{analytics.deviceBreakdown.desktop}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-blue-500 h-2.5 rounded-full" 
                            style={{
                              width: `${analytics.totalViews > 0 
                                ? (analytics.deviceBreakdown.desktop / analytics.totalViews) * 100 
                                : 0}%`,
                            }} 
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Mobile</span>
                          <span>{analytics.deviceBreakdown.mobile}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-green-500 h-2.5 rounded-full" 
                            style={{
                              width: `${analytics.totalViews > 0 
                                ? (analytics.deviceBreakdown.mobile / analytics.totalViews) * 100 
                                : 0}%`,
                            }} 
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Tablet</span>
                          <span>{analytics.deviceBreakdown.tablet}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-purple-500 h-2.5 rounded-full" 
                            style={{
                              width: `${analytics.totalViews > 0 
                                ? (analytics.deviceBreakdown.tablet / analytics.totalViews) * 100 
                                : 0}%`,
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'settings' && (
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-800">Landing Page Settings</h3>
                <div className="space-y-4 bg-white p-6 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
                    <input 
                      type="text" 
                      value={settings.title} 
                      onChange={(e) => setSettings({ ...settings, title: e.target.value })} 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input 
                      type="text" 
                      value={settings.subtitle} 
                      onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })} 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input 
                      type="text" 
                      value={settings.buttonText} 
                      onChange={(e) => setSettings({ ...settings, buttonText: e.target.value })} 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input 
                      type="url" 
                      value={settings.buttonLink} 
                      onChange={(e) => setSettings({ ...settings, buttonLink: e.target.value })} 
                      placeholder="https://example.com" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to use default click tracking only</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timer Hours</label>
                      <input 
                        type="number" 
                        value={settings.timerHours} 
                        onChange={(e) => setSettings({ ...settings, timerHours: parseInt(e.target.value) || 0 })} 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timer Minutes</label>
                      <input 
                        type="number" 
                        value={settings.timerMinutes} 
                        onChange={(e) => setSettings({ ...settings, timerMinutes: parseInt(e.target.value) || 0 })} 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timer Seconds</label>
                      <input 
                        type="number" 
                        value={settings.timerSeconds} 
                        onChange={(e) => setSettings({ ...settings, timerSeconds: parseInt(e.target.value) || 0 })} 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500" 
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex items-center gap-4">
                    <button 
                      onClick={handleSaveSettings} 
                      disabled={loading} 
                      className="bg-fuchsia-500 text-white py-3 px-6 rounded-lg hover:bg-fuchsia-600 transition-colors font-semibold flex items-center disabled:opacity-50" 
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        'Save Settings'
                      )}
                    </button>
                    {showSaveConfirmation && (
                      <div className="flex items-center gap-2 text-green-600">
                        <SafeIcon icon={FiCheckCircle} />
                        <span>Settings saved successfully!</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-200 my-6"></div>
                <div className="bg-white p-6 rounded-lg border border-red-300">
                  <h4 className="text-lg font-semibold text-red-600 mb-2">Danger Zone</h4>
                  <p className="text-sm text-gray-600 mb-4">This action is permanent and cannot be undone.</p>
                  <button 
                    onClick={handleClearStats} 
                    className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors font-semibold flex items-center gap-2" 
                  >
                    <SafeIcon icon={FiTrash2} />
                    Clear Analytics Data
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;